// src/hooks/metrics/useTokenMetrics.ts
/**
 * Derived metrics hooks - compose TokenPriceContext with metrics library
 * 
 * These hooks provide computed metrics that depend on historical data.
 * They use the centralized metrics library for calculations.
 * 
 * Usage:
 *   const { volatility, sharpe, sortino, maxDrawdown } = useRiskMetrics(90);
 *   const { alpha, beta, correlation } = useBenchmarkMetrics('BTC', 90);
 */

import { useMemo } from 'react';
import { useTokenPrice } from '@/contexts/token';
import {
  // Risk metrics
  sharpeRatio,
  sortinoRatio,
  maxDrawdownFromReturns,
  currentDrawdown,
  valueAtRisk,
  expectedShortfall,
  volatilityOfVolatility,
  // Volatility
  stdDev,
  rollingVolatility,
  rollingSharpe,
  rollingSortino,
  // Distribution
  skewness,
  kurtosis,
  // Benchmark
  computeAlphaBeta,
  computeRollingBetaCorrelation,
  upsideCapture,
  downsideCapture,
  captureSpread,
  hitRates,
  // Trends
  maDistance,
  calculateStreaks,
  calculateMomentum,
  calculateWinRate,
  // Returns
  totalReturn,
  cagr,
  winRate,
  // Types
  TRADING_DAYS,
} from '@/lib/metrics';

// ============================================================================
// RISK METRICS HOOK
// ============================================================================

export interface RiskMetricsResult {
  /** Annualized volatility */
  volatility: number | null;
  /** Annualized Sharpe ratio */
  sharpe: number | null;
  /** Annualized Sortino ratio */
  sortino: number | null;
  /** Maximum drawdown (negative decimal) */
  maxDrawdown: number | null;
  /** Current drawdown (negative decimal) */
  currentDrawdown: number | null;
  /** 95% VaR (negative decimal) */
  var95: number | null;
  /** 95% Expected Shortfall (negative decimal) */
  es95: number | null;
  /** Volatility of volatility */
  volOfVol: number | null;
  /** Skewness */
  skewness: number | null;
  /** Excess kurtosis */
  kurtosis: number | null;
  /** Win rate (% of positive days) */
  winRate: number | null;
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate risk metrics for the token
 * @param window - Number of days to use for calculations (uses most recent N days)
 */
export function useRiskMetrics(window?: number): RiskMetricsResult {
  const { token, isLoading } = useTokenPrice();
  const returns = token.returns;
  const prices = token.prices;
  
  return useMemo(() => {
    if (isLoading || returns.length === 0) {
      return {
        volatility: null,
        sharpe: null,
        sortino: null,
        maxDrawdown: null,
        currentDrawdown: null,
        var95: null,
        es95: null,
        volOfVol: null,
        skewness: null,
        kurtosis: null,
        winRate: null,
        isLoading,
      };
    }
    
    // Use window if specified, otherwise full series
    const windowReturns = window && window < returns.length 
      ? returns.slice(-window) 
      : returns;
    
    const windowPrices = window && window < prices.values.length
      ? prices.values.slice(-window)
      : prices.values;
    
    // Annualized volatility
    const dailyStd = stdDev(windowReturns);
    const vol = dailyStd !== null 
      ? dailyStd * Math.sqrt(TRADING_DAYS.CRYPTO) 
      : null;
    
    return {
      volatility: vol,
      sharpe: sharpeRatio(windowReturns, 0, true, TRADING_DAYS.CRYPTO),
      sortino: sortinoRatio(windowReturns, 0, true, TRADING_DAYS.CRYPTO),
      maxDrawdown: maxDrawdownFromReturns(windowReturns),
      currentDrawdown: currentDrawdown(windowPrices),
      var95: valueAtRisk(windowReturns, 0.95),
      es95: expectedShortfall(windowReturns, 0.95),
      volOfVol: volatilityOfVolatility(windowReturns, 30),
      skewness: skewness(windowReturns),
      kurtosis: kurtosis(windowReturns),
      winRate: winRate(windowReturns),
      isLoading,
    };
  }, [returns, prices.values, window, isLoading]);
}

// ============================================================================
// BENCHMARK METRICS HOOK
// ============================================================================

export interface BenchmarkMetricsResult {
  /** Alpha vs benchmark (annualized) */
  alpha: number | null;
  /** Beta vs benchmark */
  beta: number | null;
  /** Correlation with benchmark */
  correlation: number | null;
  /** Upside capture ratio */
  upsideCapture: number | null;
  /** Downside capture ratio */
  downsideCapture: number | null;
  /** Capture spread (upside - downside) */
  captureSpread: number | null;
  /** Hit rates */
  upHitRate: number | null;
  downHitRate: number | null;
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate metrics relative to a benchmark
 * @param benchmark - Benchmark ticker (e.g., 'BTC', 'ETH')
 * @param window - Number of days for calculations
 */
export function useBenchmarkMetrics(
  benchmark: 'BTC' | 'ETH' = 'BTC',
  window?: number
): BenchmarkMetricsResult {
  const { aligned, isLoading } = useTokenPrice();
  const alignedReturns = aligned.tokenReturns;
  const alignedBtcReturns = aligned.btcReturns;
  const alignedEthReturns = aligned.ethReturns;
  
  return useMemo(() => {
    const benchReturns = benchmark === 'BTC' ? alignedBtcReturns : alignedEthReturns;
    
    if (isLoading || alignedReturns.length === 0 || benchReturns.length === 0) {
      return {
        alpha: null,
        beta: null,
        correlation: null,
        upsideCapture: null,
        downsideCapture: null,
        captureSpread: null,
        upHitRate: null,
        downHitRate: null,
        isLoading,
      };
    }
    
    // Use window if specified
    const windowAsset = window && window < alignedReturns.length
      ? alignedReturns.slice(-window)
      : alignedReturns;
    const windowBench = window && window < benchReturns.length
      ? benchReturns.slice(-window)
      : benchReturns;
    
    const { alpha, beta } = computeAlphaBeta(windowAsset, windowBench, true, TRADING_DAYS.CRYPTO);
    const { rollingCorrelation } = computeRollingBetaCorrelation(
      windowAsset, 
      windowBench, 
      Math.min(30, windowAsset.length)
    );
    
    const rates = hitRates(windowAsset, windowBench);
    
    return {
      alpha,
      beta,
      correlation: rollingCorrelation.length > 0 
        ? rollingCorrelation[rollingCorrelation.length - 1] ?? null
        : null,
      upsideCapture: upsideCapture(windowAsset, windowBench),
      downsideCapture: downsideCapture(windowAsset, windowBench),
      captureSpread: captureSpread(windowAsset, windowBench),
      upHitRate: rates.upHitRate,
      downHitRate: rates.downHitRate,
      isLoading,
    };
  }, [alignedReturns, alignedBtcReturns, alignedEthReturns, benchmark, window, isLoading]);
}

// ============================================================================
// ROLLING METRICS HOOK
// ============================================================================

export interface RollingMetricsResult {
  /** Rolling volatility series (annualized) */
  volatility: (number | null)[];
  /** Rolling Sharpe series (annualized) */
  sharpe: (number | null)[];
  /** Rolling Sortino series (annualized) */
  sortino: (number | null)[];
  /** Dates aligned with rolling metrics */
  dates: string[];
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate rolling risk metrics
 * @param window - Rolling window size in days
 */
export function useRollingMetrics(window: number = 30): RollingMetricsResult {
  const { token, isLoading } = useTokenPrice();
  const returns = token.returns;
  const prices = token.prices;
  
  return useMemo(() => {
    if (isLoading || returns.length < window) {
      return {
        volatility: [],
        sharpe: [],
        sortino: [],
        dates: [],
        isLoading,
      };
    }
    
    const vol = rollingVolatility(returns, window, Math.floor(window * 0.6), TRADING_DAYS.CRYPTO);
    const sharpe = rollingSharpe(returns, window, Math.floor(window * 0.6), TRADING_DAYS.CRYPTO);
    const sortino = rollingSortino(returns, window, Math.floor(window * 0.6), TRADING_DAYS.CRYPTO);
    
    // Align dates (returns are 1 shorter than prices, rolling is window-1 shorter)
    const startIdx = window - 1;
    const dates = prices.dates.slice(startIdx + 1);
    
    return {
      volatility: vol,
      sharpe,
      sortino,
      dates: dates.slice(0, vol.length),
      isLoading,
    };
  }, [returns, prices.dates, window, isLoading]);
}

// ============================================================================
// TREND METRICS HOOK
// ============================================================================

export interface TrendMetricsResult {
  /** Distance from 20-day MA */
  ma20Distance: number | null;
  /** Distance from 50-day MA */
  ma50Distance: number | null;
  /** Distance from 200-day MA */
  ma200Distance: number | null;
  /** Current streak (positive = up days, negative = down days) */
  currentStreak: number | null;
  /** Longest up streak */
  longestUpStreak: number | null;
  /** Longest down streak */
  longestDownStreak: number | null;
  /** Momentum slope */
  momentumSlope: number | null;
  /** Momentum z-score */
  momentumZScore: number | null;
  /** Total return over period */
  totalReturn: number | null;
  /** CAGR */
  cagr: number | null;
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate trend and momentum metrics
 * @param window - Window for momentum calculations (optional)
 */
export function useTrendMetrics(window?: number): TrendMetricsResult {
  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;
  const returns = token.returns;
  
  return useMemo(() => {
    if (isLoading || prices.values.length === 0) {
      return {
        ma20Distance: null,
        ma50Distance: null,
        ma200Distance: null,
        currentStreak: null,
        longestUpStreak: null,
        longestDownStreak: null,
        momentumSlope: null,
        momentumZScore: null,
        totalReturn: null,
        cagr: null,
        isLoading,
      };
    }
    
    const windowPrices = window && window < prices.values.length
      ? prices.values.slice(-window)
      : prices.values;
    
    const windowReturns = window && window < returns.length
      ? returns.slice(-window)
      : returns;
    
    const streaks = calculateStreaks(windowPrices);
    const momentum = calculateMomentum(windowPrices, window);
    
    return {
      ma20Distance: maDistance(prices.values, 20),
      ma50Distance: maDistance(prices.values, 50),
      ma200Distance: maDistance(prices.values, 200),
      currentStreak: streaks?.currentStreak ?? null,
      longestUpStreak: streaks?.longestUpStreak ?? null,
      longestDownStreak: streaks?.longestDownStreak ?? null,
      momentumSlope: momentum.slope,
      momentumZScore: momentum.zScore,
      totalReturn: totalReturn(windowReturns),
      cagr: cagr(windowPrices, TRADING_DAYS.CRYPTO),
      isLoading,
    };
  }, [prices.values, returns, window, isLoading]);
}

// ============================================================================
// PERFORMANCE METRICS HOOK
// ============================================================================

export interface PerformanceMetricsResult {
  /** Win rate (% of positive return days) */
  winRate: number | null;
  /** Average return on up days */
  avgUpReturn: number | null;
  /** Average return on down days */
  avgDownReturn: number | null;
  /** Win/loss ratio */
  winLossRatio: number | null;
  /** Alpha hit rate vs BTC (% of days outperforming) */
  alphaBtcHitRate: number | null;
  /** Alpha hit rate vs ETH */
  alphaEthHitRate: number | null;
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate performance quality metrics
 * @param window - Window for calculations
 */
export function usePerformanceMetrics(window?: number): PerformanceMetricsResult {
  const { token, aligned, isLoading } = useTokenPrice();
  const returns = token.returns;
  const alignedReturns = aligned.tokenReturns;
  const alignedBtcReturns = aligned.btcReturns;
  const alignedEthReturns = aligned.ethReturns;
  
  return useMemo(() => {
    if (isLoading || returns.length === 0) {
      return {
        winRate: null,
        avgUpReturn: null,
        avgDownReturn: null,
        winLossRatio: null,
        alphaBtcHitRate: null,
        alphaEthHitRate: null,
        isLoading,
      };
    }
    
    const windowReturns = window && window < returns.length
      ? returns.slice(-window)
      : returns;
    
    const windowAligned = window && window < alignedReturns.length
      ? alignedReturns.slice(-window)
      : alignedReturns;
    
    const windowBtc = window && window < alignedBtcReturns.length
      ? alignedBtcReturns.slice(-window)
      : alignedBtcReturns;
    
    const windowEth = window && window < alignedEthReturns.length
      ? alignedEthReturns.slice(-window)
      : alignedEthReturns;
    
    const winStats = calculateWinRate(windowReturns);
    
    // Calculate alpha hit rate
    let btcHits = 0;
    let ethHits = 0;
    const n = Math.min(windowAligned.length, windowBtc.length, windowEth.length);
    
    for (let i = 0; i < n; i++) {
      if ((windowAligned[i] ?? 0) > (windowBtc[i] ?? 0)) btcHits++;
      if ((windowAligned[i] ?? 0) > (windowEth[i] ?? 0)) ethHits++;
    }
    
    return {
      winRate: winStats.winRate,
      avgUpReturn: winStats.avgUp,
      avgDownReturn: winStats.avgDown,
      winLossRatio: winStats.avgUp !== null && winStats.avgDown !== null && winStats.avgDown !== 0
        ? Math.abs(winStats.avgUp / winStats.avgDown)
        : null,
      alphaBtcHitRate: n > 0 ? (btcHits / n) * 100 : null,
      alphaEthHitRate: n > 0 ? (ethHits / n) * 100 : null,
      isLoading,
    };
  }, [returns, alignedReturns, alignedBtcReturns, alignedEthReturns, window, isLoading]);
}

